import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/EditOutlined'
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined'
import LinkOffIcon from '@mui/icons-material/LinkOffOutlined'
import KeyIcon from '@mui/icons-material/KeyOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import { MEMBER_STATUS_LABELS, type PlatformUserDetail } from '@iobiz/shared'
import { api } from '../api/client.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'
import { ChoixPortrait } from '../ui/ChoixPortrait.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'

/**
 * Fiche d'un compte de la plateforme.
 *
 * ⭐ Son intérêt tient à la table du bas : le PÉRIMÈTRE. « Ce compte touche
 * quelles boutiques, avec quel rôle » est la question qu'on se pose devant un
 * compte suspect ou avant de le supprimer — pas son numéro de téléphone.
 */

function dateCourte(iso: string | null): string {
  if (!iso) return 'Jamais'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function Field({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value?.trim() ? value : '—'}</Typography>
    </Box>
  )
}

export function PlatformUserDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<PlatformUserDetail | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [edition, setEdition] = useState(false)
  const [suppression, setSuppression] = useState(false)
  const [motDePasse, setMotDePasse] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [affectation, setAffectation] = useState(false)

  const charger = useCallback(async () => {
    if (!id) return
    try {
      setUser(await api.get<PlatformUserDetail>(`/admin/users/${id}`))
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Chargement impossible.')
    }
  }, [id])

  useEffect(() => {
    void charger()
  }, [charger])

  if (!user) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        {erreur ? <Alert severity="error">{erreur}</Alert> : <CircularProgress size={28} />}
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 6 }}>
      <Link
        component="button"
        variant="body2"
        underline="hover"
        onClick={() => navigate('/admin/utilisateurs')}
        sx={{ mb: 3, display: 'block' }}
      >
        ← Comptes
      </Link>

      {erreur && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
          {erreur}
        </Alert>
      )}

      {info && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <VignetteFiche
            images={user.avatarUrl ? [user.avatarUrl] : []}
            repli={
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'text.disabled' }}>
                {user.displayName.slice(0, 2).toUpperCase()}
              </Typography>
            }
          />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h1">{user.displayName}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {user.email} · Créé le {dateCourte(user.createdAt)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                color={user.status === 'active' ? 'success' : 'warning'}
                label={user.status === 'active' ? 'Actif' : user.status}
              />
              {user.isPlatformAdmin && (
                <Chip size="small" color="primary" label="Administrateur plateforme" />
              )}
              {!user.hasPassword && (
                <Chip size="small" variant="outlined" label="Sans mot de passe" />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEdition(true)}>
              Modifier
            </Button>
            <IconButton
              aria-label="Autres actions"
              onClick={(e) => setMenu(e.currentTarget)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 4,
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          alignItems: 'start',
        }}
      >
        <Paper sx={{ p: 6, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4, flexWrap: 'wrap' }}>
            <Typography variant="h3" sx={{ m: 0, flex: 1 }}>
              Périmètre — {user.memberships.length} boutique
              {user.memberships.length > 1 ? 's' : ''}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<StorefrontIcon />}
              onClick={() => setAffectation(true)}
            >
              Rattacher à une boutique
            </Button>
          </Box>
          {user.memberships.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {user.isPlatformAdmin
                ? 'Aucune appartenance — mais son statut d’administrateur lui ouvre TOUTES les boutiques.'
                : 'Ce compte n’est rattaché à aucune boutique : il ne peut rien voir.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Boutique</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Depuis</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {user.memberships.map((m) => (
                  <TableRow key={m.storeId} hover>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => navigate(`/s/${m.slug}/utilisateurs`)}
                      >
                        {m.storeName}
                      </Link>
                    </TableCell>
                    <TableCell>{m.roleName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={m.status === 'active' ? 'success' : 'default'}
                        label={MEMBER_STATUS_LABELS[m.status]}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {m.joinedAt ? dateCourte(m.joinedAt) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={`Détacher de ${m.storeName}`}
                        onClick={async () => {
                          try {
                            setUser(
                              await api.delete<PlatformUserDetail>(
                                `/admin/users/${user.id}/memberships/${m.storeId}`,
                              ),
                            )
                            setInfo(`Détaché de ${m.storeName}.`)
                          } catch (e) {
                            setErreur(e instanceof Error ? e.message : 'Détachement impossible.')
                          }
                        }}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {user.isPlatformAdmin && user.memberships.length > 0 && (
            <Alert severity="info" sx={{ mt: 4 }}>
              Administrateur de plateforme : son accès ne se limite pas à cette liste, il atteint
              toutes les boutiques. Chacun de ces accès est journalisé.
            </Alert>
          )}
        </Paper>

        <Paper sx={{ p: 6, borderRadius: 3 }}>
          <Typography variant="h3" sx={{ mb: 4 }}>
            Compte
          </Typography>
          <Field label="Email" value={user.email} />
          <Field label="Prénom" value={user.firstName} />
          <Field label="Nom" value={user.lastName} />
          <Field label="Téléphone" value={user.phone} />
          <Field label="Dernière connexion" value={dateCourte(user.lastLoginAt)} />
          <Field label="Mot de passe" value={user.hasPassword ? 'Défini' : 'Jamais défini'} />
        </Paper>
      </Box>

      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={async () => {
            setMenu(null)
            try {
              const r = await api.post<{ envoye: boolean; url: string | null }>(
                `/admin/users/${user.id}/password`,
              )
              // Courriel parti : le lien n'a pas à s'afficher ici. Sinon on le
              // montre, pour que l'administrateur puisse le transmettre.
              if (r.envoye) {
                setErreur(null)
                setInfo(`Lien de réinitialisation envoyé à ${user.email}.`)
              } else {
                setMotDePasse(r.url)
              }
              await charger()
            } catch (e) {
              setErreur(e instanceof Error ? e.message : 'Réinitialisation impossible.')
            }
          }}
        >
          <KeyIcon sx={{ fontSize: 18, mr: 2 }} />
          Envoyer un lien de réinitialisation
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setMenu(null)
            try {
              await api.patch(`/admin/users/${user.id}`, {
                status: user.status === 'disabled' ? 'active' : 'disabled',
              })
              await charger()
            } catch (e) {
              setErreur(e instanceof Error ? e.message : 'Action impossible.')
            }
          }}
        >
          {user.status === 'disabled' ? 'Réactiver le compte' : 'Désactiver le compte'}
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setMenu(null)
            setSuppression(true)
          }}
        >
          Supprimer définitivement
        </MenuItem>
      </Menu>

      <BoiteAffectation
        open={affectation}
        userId={user.id}
        onClose={() => setAffectation(false)}
        onFait={(maj) => {
          setUser(maj)
          setAffectation(false)
        }}
        onErreur={setErreur}
      />

      <BoiteEdition
        open={edition}
        user={user}
        onClose={() => setEdition(false)}
        onEnregistre={async () => {
          setEdition(false)
          await charger()
        }}
        onErreur={setErreur}
      />

      <ConfirmDialog
        open={suppression}
        title={`Supprimer « ${user.displayName} » ?`}
        message={
          user.memberships.length > 0
            ? `Ce compte est rattaché à ${user.memberships.length} boutique(s). Ses appartenances et ses sessions seront supprimées avec lui. Cette action est définitive.`
            : 'Ses sessions seront supprimées avec lui. Cette action est définitive.'
        }
        confirmLabel="Supprimer"
        onClose={() => setSuppression(false)}
        onConfirm={async () => {
          await api.delete(`/admin/users/${user.id}`)
          navigate('/admin/utilisateurs')
        }}
      />

      <Dialog open={motDePasse !== null} onClose={() => setMotDePasse(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Lien de réinitialisation</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            L’envoi par courriel a échoué ou n’est pas configuré. Transmettez ce lien à la
            personne : il est valable une heure, ne sert qu’une fois, et n’est affiché qu’ici.
          </Alert>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Typography className="tabular" sx={{ flex: 1, fontWeight: 700, fontSize: 18 }}>
              {motDePasse}
            </Typography>
            <IconButton
              aria-label="Copier"
              onClick={() => void navigator.clipboard.writeText(motDePasse ?? '')}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMotDePasse(null)}>
            J’ai noté
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function BoiteEdition({
  open,
  user,
  onClose,
  onEnregistre,
  onErreur,
}: {
  open: boolean
  user: PlatformUserDetail
  onClose: () => void
  onEnregistre: () => Promise<void>
  onErreur: (m: string) => void
}): JSX.Element {
  const [email, setEmail] = useState(user.email)
  const [prenom, setPrenom] = useState(user.firstName)
  const [nom, setNom] = useState(user.lastName)
  const [tel, setTel] = useState(user.phone ?? '')
  const [admin, setAdmin] = useState(user.isPlatformAdmin)
  const [portrait, setPortrait] = useState<string | null>(user.avatarUrl)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (open) {
      setEmail(user.email)
      setPrenom(user.firstName)
      setNom(user.lastName)
      setTel(user.phone ?? '')
      setAdmin(user.isPlatformAdmin)
      setPortrait(user.avatarUrl)
    }
  }, [open, user])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Modifier le compte</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 4, pt: 2 }}>
        {/* La photo EN PREMIER : c'est elle qui identifiera la personne dans
            toute l'application une fois connectée. */}
        <ChoixPortrait value={portrait} onChange={setPortrait} />
        <TextField
          label="Adresse e-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Prénom" value={prenom} onChange={(e) => setPrenom(e.target.value)} />
          <TextField label="Nom" value={nom} onChange={(e) => setNom(e.target.value)} />
        </Box>
        <TextField label="Téléphone" value={tel} onChange={(e) => setTel(e.target.value)} />
        <FormControlLabel
          control={<Switch checked={admin} onChange={(e) => setAdmin(e.target.checked)} />}
          label="Administrateur de la plateforme"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={envoi}
          onClick={async () => {
            setEnvoi(true)
            try {
              await api.patch(`/admin/users/${user.id}`, {
                email: email.trim(),
                firstName: prenom.trim(),
                lastName: nom.trim(),
                phone: tel.trim(),
                isPlatformAdmin: admin,
                avatarUrl: portrait,
              })
              await onEnregistre()
            } catch (e) {
              onErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function BoiteAffectation({
  open,
  userId,
  onClose,
  onFait,
  onErreur,
}: {
  open: boolean
  userId: string
  onClose: () => void
  onFait: (u: PlatformUserDetail) => void
  onErreur: (m: string) => void
}): JSX.Element {
  const [options, setOptions] = useState<{
    stores: { id: string; name: string; slug: string }[]
    roles: { id: string; name: string; code: string }[]
  } | null>(null)
  const [storeId, setStoreId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!open) return
    void (async () => {
      try {
        const o = await api.get<{
          stores: { id: string; name: string; slug: string }[]
          roles: { id: string; name: string; code: string }[]
        }>('/admin/affectations/options')
        setOptions(o)
        setStoreId(o.stores[0]?.id ?? '')
        // « Employé » par défaut : le rôle le plus courant, et le moins
        // dangereux si l'on valide sans réfléchir.
        setRoleId(o.roles.find((r) => r.code === 'employee')?.id ?? o.roles[0]?.id ?? '')
      } catch (e) {
        onErreur(e instanceof Error ? e.message : 'Chargement impossible.')
      }
    })()
  }, [open, onErreur])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rattacher à une boutique</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 4, pt: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Le compte existe déjà : on lui ouvre l’accès à une boutique de plus, avec le rôle qu’il
          y tiendra. Ses autres rattachements ne changent pas.
        </Typography>
        <TextField
          select
          label="Boutique"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          {(options?.stores ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </TextField>
        <TextField
          select
          label="Rôle"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          {(options?.roles ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!storeId || !roleId || envoi}
          onClick={async () => {
            setEnvoi(true)
            try {
              onFait(
                await api.post<PlatformUserDetail>(`/admin/users/${userId}/memberships`, {
                  storeId,
                  roleId,
                }),
              )
            } catch (e) {
              onErreur(e instanceof Error ? e.message : 'Rattachement impossible.')
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Rattacher
        </Button>
      </DialogActions>
    </Dialog>
  )
}
